import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams, useNavigate } from 'react-router-dom';
import AdminLayout from '@/components/layout/AdminLayout';
import apiService from '@/services/api';
import { Button } from '@/components/ui/button';
import { Maximize2, X, CheckCircle, Clock, AlertCircle, Edit, Save } from 'lucide-react';

const AdminVariationDetailPage = () => {
  const { variationId } = useParams<{ variationId: string }>();
  const navigate = useNavigate();
  const [internalNotes, setInternalNotes] = useState('');
  const [variationData, setVariationData] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  // Fetch variation data from database
  useEffect(() => {
    const fetchVariation = async () => {
      if (!variationId) return;
      try {
        const response = await apiService.request(`/admin/variations/${variationId}`, 'GET');
        if (response.success && response.data) {
          setVariationData(response.data);
        }
      } catch (error) {
        console.error('Failed to fetch variation:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchVariation();
  }, [variationId]);

  const defaultSiteImages: string[] = [];

  // Handler: Approve variation
  const handleApproveVariation = async () => {
    try {
      const response = await apiService.request(`/admin/variations/${variationId}/approve`, 'POST', {
        status: 'APPROVED',
        approvedBy: localStorage.getItem('adminId'),
        approvedAt: new Date().toISOString()
      });
      if (response.success) {
        alert('✓ Variation approved successfully');
        window.location.reload();
      } else {
        alert('Error: ' + response.message);
      }
    } catch (error: any) {
      console.error('Approve variation error:', error);
      alert('Failed to approve variation: ' + error.message);
    }
  };

  // Handler: Reject variation
  const handleRejectVariation = async () => {
    try {
      const response = await apiService.request(`/admin/variations/${variationId}/reject`, 'POST', {
        status: 'REJECTED',
        rejectionReason: internalNotes || 'Admin rejection',
        rejectedBy: localStorage.getItem('adminId'),
        rejectedAt: new Date().toISOString()
      });
      if (response.success) {
        alert('✓ Variation rejected successfully');
        window.location.reload();
      } else {
        alert('Error: ' + response.message);
      }
    } catch (error: any) {
      console.error('Reject variation error:', error);
      alert('Failed to reject variation: ' + error.message);
    }
  };

  // Handler: Request more information
  const handleRequestInfo = async () => {
    try {
      const response = await apiService.request(`/admin/variations/${variationId}/request-info`, 'POST', {
        message: internalNotes || 'Additional information needed',
        requestedBy: localStorage.getItem('adminId'),
        requestedAt: new Date().toISOString()
      });
      if (response.success) {
        alert('✓ Information request sent successfully');
        window.location.reload();
      } else {
        alert('Error: ' + response.message);
      }
    } catch (error: any) {
      console.error('Request info error:', error);
      alert('Failed to request information: ' + error.message);
    }
  };

  // Handler: Escalate variation
  const handleEscalateVariation = async () => {
    try {
      const response = await apiService.request(`/admin/variations/${variationId}/escalate`, 'POST', {
        escalationLevel: 'MANAGER',
        escalationReason: internalNotes || 'Admin escalation',
        escalatedBy: localStorage.getItem('adminId'),
        escalatedAt: new Date().toISOString()
      });
      if (response.success) {
        alert('✓ Variation escalated successfully');
        window.location.reload();
      } else {
        alert('Error: ' + response.message);
      }
    } catch (error: any) {
      console.error('Escalate variation error:', error);
      alert('Failed to escalate variation: ' + error.message);
    }
  };

  // Use fetched images or defaults
  const displayImages = (variationData?.images && variationData.images.length > 0) ? variationData.images : defaultSiteImages;

  const workflowSteps = [
    { id: 1, label: 'SUBMITTED', status: 'completed' },
    { id: 2, label: 'PLATFORM REVIEW', status: 'current' },
    { id: 3, label: 'DECISION ISSUED', status: 'pending' },
    { id: 4, label: 'CUSTOMER APPROVED', status: 'pending' }
  ];

  return (
    <>
      <Helmet>
        <title>Variation Detail - Virewire</title>
      </Helmet>

      <AdminLayout>
        <div className="bg-gray-50 min-h-screen p-8">
          {/* Header */}
          <div className="flex items-start justify-between mb-8">
            {loading ? (
              <div className="text-gray-600">Loading...</div>
            ) : !variationData ? (
              <div className="text-red-600 font-semibold">Variation not found</div>
            ) : (
            <div>
              <h1 className="text-4xl font-bold text-gray-900 mb-4">{variationData?.variation_ref || `VAR #${variationId}`}</h1>
              <div className="flex items-center gap-4 flex-wrap">
                <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full font-bold text-xs uppercase tracking-wide ${
                  variationData?.status === 'approved' ? 'bg-green-100 text-green-700' :
                  variationData?.status === 'rejected' ? 'bg-red-100 text-red-700' :
                  'bg-blue-100 text-blue-700'
                }`}>
                  ● {variationData?.status === 'approved' ? 'Approved' : variationData?.status === 'rejected' ? 'Rejected' : 'Under Review'}
                </span>
                {variationData?.work_paused && (
                  <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-red-100 text-red-700 font-bold text-xs uppercase tracking-wide">
                    ⚠ Work Paused
                  </span>
                )}
              </div>
              <div className="flex items-center gap-6 mt-4 text-sm text-gray-600">
                <div className="flex items-center gap-2">
                  <span>👁</span>
                  <span>Linked Job: <span className="font-mono font-bold text-gray-900">{variationData?.job_id || '—'}</span></span>
                </div>
                <div className="flex items-center gap-2">
                  <span>🏢</span>
                  <span>Provider: <span className="font-bold text-gray-900">{variationData?.provider_name || '—'}</span></span>
                </div>
              </div>
            </div>
            )}
            <div className="flex items-center gap-3">
              <button className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                <Maximize2 className="w-5 h-5 text-gray-600" />
              </button>
              <button onClick={() => navigate('/admin/variations')} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                <X className="w-5 h-5 text-gray-600" />
              </button>
            </div>
          </div>

          {/* Workflow Steps */}
          <div className="mb-8 bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              {workflowSteps.map((step, idx) => (
                <div key={step.id} className="flex items-center gap-4">
                  <div className="flex flex-col items-center">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm mb-2 ${
                      step.status === 'completed'
                        ? 'bg-green-600 text-white'
                        : step.status === 'current'
                        ? 'bg-teal-600 text-white'
                        : 'bg-gray-300 text-gray-600'
                    }`}>
                      {step.status === 'completed' ? '✓' : step.id}
                    </div>
                    <span className={`text-xs font-bold uppercase tracking-widest text-center max-w-[100px] ${
                      step.status === 'completed'
                        ? 'text-green-600'
                        : step.status === 'current'
                        ? 'text-teal-600'
                        : 'text-gray-500'
                    }`}>
                      {step.label}
                    </span>
                  </div>
                  {idx < workflowSteps.length - 1 && (
                    <div className={`h-1 w-16 ${
                      step.status === 'completed'
                        ? 'bg-green-600'
                        : 'bg-gray-300'
                    }`}></div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Main Content Grid */}
          <div className="grid grid-cols-3 gap-8">
            {/* Left Column - Scope Comparison */}
            <div className="col-span-1 space-y-6">
              {/* Original Scope */}
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-3 h-3 bg-blue-600 rounded-full"></div>
                  <h3 className="text-xs font-bold text-gray-700 uppercase tracking-widest">Original Agreed Scope</h3>
                </div>
                <p className="text-sm text-gray-700 mb-4 leading-relaxed">
                  {variationData?.original_scope || '—'}
                </p>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-xs font-semibold text-gray-600 uppercase mb-2">Total Value</p>
                  <p className="text-2xl font-bold text-gray-900">{variationData?.original_amount ? `$${Number(variationData.original_amount).toFixed(2)}` : '—'}</p>
                </div>
              </div>

              {/* Provider's Requested Adjustment */}
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-3 h-3 bg-orange-600 rounded-full"></div>
                  <h3 className="text-xs font-bold text-gray-700 uppercase tracking-widest">Provider's Requested Adjustment</h3>
                </div>
                <p className="text-sm text-gray-700 mb-4 leading-relaxed">
                  {variationData?.requested_scope || '—'}
                </p>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-xs font-semibold text-gray-600 uppercase mb-2">Proposed Value</p>
                  <p className="text-2xl font-bold text-gray-900">{variationData?.requested_amount ? `$${Number(variationData.requested_amount).toFixed(2)}` : '—'}</p>
                </div>
              </div>

              {/* Delta Summary */}
              {variationData?.original_amount && variationData?.requested_amount && (
              <div className="bg-green-600 text-white rounded-xl p-6 shadow-lg">
                <p className="text-xs font-bold uppercase tracking-widest mb-4 opacity-90">Delta Summary</p>
                <div className="flex items-center gap-8">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wider opacity-75 mb-1">Cost Delta</p>
                    <p className="text-3xl font-bold">{Number(variationData.requested_amount) - Number(variationData.original_amount) > 0 ? '+' : ''}${(Number(variationData.requested_amount) - Number(variationData.original_amount)).toFixed(2)}</p>
                  </div>
                  {variationData?.time_delta && (
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wider opacity-75 mb-1">Time Delta</p>
                    <p className="text-3xl font-bold">{variationData.time_delta}</p>
                  </div>
                  )}
                </div>
              </div>
              )}
            </div>

            {/* Center Column - Evidence & Notes */}
            <div className="col-span-1 space-y-6">
              {/* Site Evidence Gallery */}
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h3 className="text-xs font-bold text-gray-700 uppercase tracking-widest mb-4">Site Evidence</h3>
                <div className="grid grid-cols-2 gap-3">
                  {siteImages.map((img, idx) => (
                    <div key={idx} className="aspect-square rounded-lg overflow-hidden border border-gray-200 hover:border-teal-600 transition-colors cursor-pointer">
                      <img src={img} alt={`Site evidence ${idx + 1}`} className="w-full h-full object-cover hover:scale-105 transition-transform" />
                    </div>
                  ))}
                </div>
              </div>

              {/* Provider Notes */}
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h3 className="text-xs font-bold text-gray-700 uppercase tracking-widest mb-4">Provider Notes</h3>
                <p className="text-sm text-gray-700 leading-relaxed">
                  {variationData?.provider_notes || '—'}
                </p>
              </div>
            </div>

            {/* Right Column - Decision Panel */}
            <div className="col-span-1 space-y-6">
              {/* Case Assignment */}
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h3 className="text-xs font-bold text-gray-700 uppercase tracking-widest mb-4">Case Assignment</h3>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold text-sm shadow-sm">
                    SJ
                  </div>
                  <div>
                    <p className="font-bold text-gray-900">{variationData?.assigned_to_name || variationData?.assigned_to || 'Unassigned'}</p>
                    <p className="text-xs text-gray-600">{variationData?.assigned_to_role || 'Compliance Officer'}</p>
                  </div>
                </div>
              </div>

              {/* SLA Timer */}
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h3 className="text-xs font-bold text-gray-700 uppercase tracking-widest mb-4">SLA Time Remaining</h3>
                <div className="flex items-center gap-3">
                  <Clock className="w-6 h-6 text-red-600" />
                  <p className="text-4xl font-bold text-red-600 font-mono">00:44:12</p>
                </div>
              </div>

              {/* Final Decision Actions */}
              <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-3">
                <h3 className="text-xs font-bold text-gray-700 uppercase tracking-widest mb-4">Final Decision</h3>
                
                <Button onClick={handleApproveVariation} className="w-full bg-green-600 hover:bg-green-700 text-white flex items-center justify-center gap-2">
                  <CheckCircle className="w-5 h-5" />
                  Approve Variation
                </Button>

                <Button onClick={handleRequestInfo} className="w-full bg-blue-600 hover:bg-blue-700 text-white flex items-center justify-center gap-2">
                  <AlertCircle className="w-5 h-5" />
                  Request More Info
                </Button>

                <Button onClick={handleEscalateVariation} className="w-full bg-amber-600 hover:bg-amber-700 text-white flex items-center justify-center gap-2">
                  <Edit className="w-5 h-5" />
                  Amend & Approve
                </Button>

                <Button onClick={handleRejectVariation} className="w-full bg-red-600 hover:bg-red-700 text-white flex items-center justify-center gap-2">
                  ✕
                  Reject Variation
                </Button>
              </div>

              {/* Internal Audit Notes */}
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h3 className="text-xs font-bold text-gray-700 uppercase tracking-widest mb-4">Internal Audit Notes</h3>
                <textarea
                  value={internalNotes}
                  onChange={(e) => setInternalNotes(e.target.value)}
                  placeholder="Enter notes only visible to internal staff..."
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-600 resize-none bg-gray-50 mb-3"
                  rows={4}
                />
                <Button className="w-full bg-gray-900 hover:bg-gray-800 text-white font-bold py-2 rounded-lg flex items-center justify-center gap-2">
                  <Save className="w-4 h-4" />
                  Save Note
                </Button>
              </div>
            </div>
          </div>
        </div>
      </AdminLayout>
    </>
  );
};

export default AdminVariationDetailPage;
